import * as Sentry from "@sentry/react";

import { getPhiSafeErrorMetadata } from "./phi-safe-error-metadata";

/**
 * Context safe to attach to a report: opaque record ids, statuses, counts, flags. Never a value read
 * from a resource — no codes, displays, names, dates, or free text.
 */
type PhiSafeContext = Record<string, string | number | boolean | undefined>;

/** Session states the global beforeSend in index.tsx drops as noise rather than bugs. */
const IGNORED_OUTCOME_IDS = ["unauthorized", "forbidden"];
const IGNORED_STATUSES = [401, 403];

/**
 * Reports a failure to Sentry without sending anything the resources involved carried.
 *
 * Over patient data, the error itself cannot be reported: a Medplum failure throws an
 * OperationOutcomeError whose message, diagnostics, and details quote what the server rejected —
 * diagnosis codes, patient references, element values — which is PHI. So the original error never
 * reaches Sentry, and cannot be serialized by it. What gets sent is the caller's own fixed message,
 * the error's class name, the HTTP status, the OperationOutcome issue codes, and whatever non-PHI
 * context the caller passes.
 *
 * @param message Fixed description of the operation that failed. Must not interpolate resource data.
 * @param error The caught error. Read for non-PHI metadata only, never forwarded.
 * @param context Non-PHI details worth having on the report, e.g. opaque resource ids.
 * @param level Sentry severity. Defaults to the error level; pass `"warning"` for a gap that leaves the user's work intact.
 */
export const captureExceptionWithoutPhi = (
  message: string,
  error: unknown,
  context?: PhiSafeContext,
  level?: Sentry.SeverityLevel,
): void => {
  const errorMetadata = getPhiSafeErrorMetadata(error);

  // Scrubbing replaces the original error, so the global beforeSend filter can no longer recognize an
  // expired or forbidden session. Dropping it here keeps these reports out of Sentry the same way.
  if (
    IGNORED_STATUSES.includes(errorMetadata.status ?? 0) ||
    IGNORED_OUTCOME_IDS.includes(errorMetadata.outcomeId ?? "")
  ) {
    return;
  }

  Sentry.captureException(new Error(message), {
    ...(level ? { level } : {}),
    extra: {
      ...context,
      ...errorMetadata,
    },
  });
};
